import React, { useEffect, useState } from "react";
import {
  Avatar,
  Typography,
  Button,
  Paper,
  Box,
  Skeleton,
  TextField,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../auth/firebase-config/firebase";
import { useTranslation } from "react-i18next";
import LogoutIcon from "@mui/icons-material/Logout";
import { colors, fonts, size } from "../ColorFont/color";
import { Edit, Upload, UploadFile } from "@mui/icons-material";
import { url } from "../ConstUrl/Url";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";

const Profile = ({ userData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [userDetails, setUserDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isModalClosed, setIsModalClosed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(userData);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800); // Simulate loading for 800ms
    return () => clearTimeout(timer);
  }, []);

  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size if needed
    if (!file.type.match("image.*")) {
      toast.error("Please select an image file", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setUploading(true);
    const formDataData = new FormData();
    formDataData.append("profilePicture", file);

    try {
      const response = await fetch(
        `${url}/api/users/${userData._id}/profile-picture`,
        {
          method: "PUT",
          body: formDataData,
        },
      );
      const data = await response.json();

      if (response.ok) {
        setFormData((prev) => ({
          ...prev,
          profilePicture: data.user.profilePicture,
        }));
        toast.success("Profile picture updated successfully!", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error(data.message || "Failed to update profile picture", {
          position: "top-center",
          autoClose: 4000,
        });
      }
    } catch (error) {
      toast.error("Network error: Could not upload profile picture", {
        position: "top-center",
        autoClose: 4000,
      });
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing((prev) => !prev);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserDetails({
        username: user.displayName || "User",
        email: user.email || "N/A",
        phoneNumber: user.phoneNumber || "N/A",
        photoURL: user.photoURL,
      });
    }

    setIsModalClosed(false);
  }, [userData, isModalClosed, showEditModal]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.reload();
      navigate("/home");
    } catch (error) {}
  };

  const handleSubmit = async () => {
    setIsEditing((prev) => !prev);
    const userId = userData._id;
    try {
      const response = await fetch(
        `${url}/api/users/update-profile/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const data = await response.json();

      if (response.ok) {
        alert(data.message);
        closeModal();
        window.location.reload();
      } else {
        alert("Error updating profile: " + data.message);
      }
    } catch (error) {
      alert("Error updating profile");
    }
  };

  const closeModal = () => {
    setShowEditModal(false);
    setIsModalClosed((prev) => !prev);
  };

  if (loading) {
    return (
      <Box sx={{ background: colors.background3, flex: 1 }}>
        <Box sx={{ margin: "14px" }}>
          {/* Header Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: isMobile ? 1 : 2,
                marginBottom: 5,
              }}
            >
              <Skeleton
                variant="text"
                width={isMobile ? 120 : 150}
                height={isMobile ? 32 : 40}
                sx={{
                  borderRadius: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                }}
              />
              <Skeleton
                variant="rectangular"
                width={isMobile ? 100 : 120}
                height={isMobile ? 36 : 40}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                }}
              />
            </Box>
          </motion.div>

          {/* User Info Section Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          >
            <Box sx={{ marginBottom: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: isMobile ? "center" : "flex-end",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                    gap: isMobile ? 1.5 : 2,
                    justifyContent: "flex-end",
                  }}
                >
                  {/* User Details Skeleton */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      alignItems: isMobile ? "center" : "flex-start",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width={isMobile ? 180 : 220}
                      height={isMobile ? 24 : 28}
                      sx={{
                        borderRadius: 1,
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        mb: 0.5,
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={isMobile ? 140 : 180}
                      height={isMobile ? 16 : 18}
                      sx={{
                        borderRadius: 1,
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    />
                  </Box>

                  {/* Avatar Skeleton */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Skeleton
                        variant="circular"
                        width={isMobile ? 60 : 70}
                        height={isMobile ? 60 : 70}
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        }}
                      />
                      <Skeleton
                        variant="circular"
                        width={isMobile ? 22 : 25}
                        height={isMobile ? 22 : 25}
                        sx={{
                          position: "absolute",
                          bottom: 2,
                          right: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.5)",
                        }}
                      />
                    </Box>
                    <Skeleton
                      variant="text"
                      width={isMobile ? 60 : 80}
                      height={isMobile ? 16 : 18}
                      sx={{
                        mt: 0.5,
                        borderRadius: 1,
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </motion.div>

          {/* Upload Button Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "end", marginBottom: 2 }}
            >
              <Skeleton
                variant="rectangular"
                width={isMobile ? 140 : 160}
                height={isMobile ? 36 : 40}
                sx={{
                  borderRadius: "50px",
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                }}
              />
            </Box>
          </motion.div>

          {/* Form Fields Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
          >
            <Box>
              {/* First Row - First Name & Last Name */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? 2 : 10,
                  marginBottom: 3,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width={isMobile ? 80 : 100}
                    height={isMobile ? 20 : 24}
                    sx={{
                      borderRadius: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={isMobile ? 48 : 56}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width={isMobile ? 80 : 100}
                    height={isMobile ? 20 : 24}
                    sx={{
                      borderRadius: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={isMobile ? 48 : 56}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                </Box>
              </Box>

              {/* Additional Form Rows */}
              {[1, 2, 3, 4].map((index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.5,
                    ease: "easeOut",
                    delay: 0.8 + index * 0.1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? 2 : 10,
                      marginBottom: 3,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        width={isMobile ? 60 : 80}
                        height={isMobile ? 20 : 24}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                          mb: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        height={isMobile ? 48 : 56}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        width={isMobile ? 60 : 80}
                        height={isMobile ? 20 : 24}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                          mb: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        height={isMobile ? 48 : 56}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        }}
                      />
                    </Box>
                  </Box>
                </motion.div>
              ))}

              {/* Action Buttons Skeleton */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 1.2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "flex-end",
                    gap: 2,
                    marginTop: 2,
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={isMobile ? 100 : 120}
                    height={isMobile ? 40 : 44}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={isMobile ? 120 : 140}
                    height={isMobile ? 40 : 44}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </Box>
      </Box>
    );
  }

  if (!userDetails) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 5,
            borderBottom: "1px solid gray",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 50,
              width: "100%",
              marginBottom: 5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 50,
                }}
              >
                <Button>
                  <Skeleton variant="text" sx={{ width: 80, height: 40 }} />
                </Button>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Skeleton variant="text" sx={{ width: 200, height: 50 }} />
                  <Skeleton variant="text" sx={{ width: 200, height: 50 }} />
                </Box>
              </Box>
              <Avatar>
                <Skeleton variant="circular" sx={{ width: 60, height: 60 }} />
              </Avatar>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                marginBottom: 5,
              }}
            >
              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>

              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>

              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                marginBottom: 5,
              }}
            >
              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>

              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>

              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                marginBottom: 5,
              }}
            >
              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>

              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>

              <Box>
                <Skeleton sx={{ width: 200, height: 100 }} />
              </Box>
            </Box>
          </Box>

          <Button>
            <Skeleton sx={{ width: 100, height: 50 }} />
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ background: colors.background3, flex: 1 }}>
      <Box sx={{ margin: "14px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: isMobile ? 1 : 2, // Adjusts spacing based on screen size
            marginBottom: 5,
          }}
        >
          <Typography
            sx={{
              fontFamily: fonts.poppins,
              fontSize: isMobile ? size.subtitle : size.title, // Adjusts size for mobile
              fontWeight: 600,
              color: colors.primary_dark,
            }}
          >
            {t("user")}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleSignOut}
            sx={{
              fontFamily: fonts.poppins,
              background: "#C91A1A",
              borderRadius: 2,
              padding: isMobile ? "6px 16px" : "8px 20px", // Adjust padding
              gap: 0.5,
              fontSize: isMobile ? size.body : size.formLabel, // Adjust font size
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              minWidth: isMobile ? "auto" : "unset", // Prevents button from stretching on smaller screens
            }}
          >
            <LogoutIcon
              sx={{ fontSize: isMobile ? size.smallLabel : size.label }}
            />
            {t("signout")}
          </Button>
        </Box>

        <Box sx={{ marginBottom: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: isMobile ? "center" : "flex-end", // Center on mobile, align right on desktop
              alignItems: "center",
            }}
          >
            {/* User Info Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row", // Stack vertically on mobile
                alignItems: "center",
                gap: isMobile ? 1.5 : 2, // Adjust gap for better spacing
                justifyContent: "flex-end",
              }}
            >
              {/* User Details */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: isMobile ? "center" : "flex-start", // Center text on mobile
                  textAlign: isMobile ? "center" : "left", // Align text for readability
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? 18 : 20, // Adjust font size
                    color: colors.primary_dark,
                    fontWeight: 600,
                    fontFamily: fonts.poppins,
                  }}
                >
                  {t("welcome")}, {formData?.firstName && formData?.lastName ? `${formData.firstName} ${formData.lastName}` : (formData?.firstName || formData?.lastName || userDetails.username)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? 12 : 13,
                    fontFamily: fonts.poppins,
                  }}
                >
                  {t("Email")}: {userDetails.email}
                </Typography>
              </Box>

              {/* Avatar Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Avatar Container (Relative for Edit Positioning) */}
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    src={
                      formData.profilePicture
                        ? `${url}/${formData.profilePicture.replace(/\\/g, "/")}`
                        : formData.photoURL
                    }
                    sx={{
                      width: isMobile ? 80 : 70,
                      height: isMobile ? 80 : 70,
                    }}
                  >
                    {formData?.firstName
                      ? formData.firstName.charAt(0).toUpperCase()
                      : userDetails.username.charAt(0).toUpperCase()}
                  </Avatar>
                  {/* Upload Button */}

                  {/* Edit Icon Positioned on Avatar */}
                  {!isEditing && (
                    <Tooltip title="Edit">
                      <Edit
                        onClick={handleEditClick}
                        sx={{
                          position: "absolute",
                          bottom: 2,
                          right: 2,
                          background: "#fff",
                          borderRadius: "50%",
                          padding: "4px",
                          fontSize: isMobile ? 22 : 25, // Adjust icon size
                          cursor: "pointer",
                          boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                          color: colors.primary_dark,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>

                {/* Role Text Below Avatar */}
                <Typography
                  sx={{
                    fontSize: size.text,
                    fontWeight: 600,
                    fontFamily: fonts.poppins,
                    marginLeft: 0.5,
                    color: colors.primary_dark,
                  }}
                >
                  {formData.role}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {formData && (
          <>
            <Box
              sx={{ display: "flex", justifyContent: "end", marginBottom: 2 }}
            >
              {isEditing && (
                <>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="profile-pic-upload"
                    type="file"
                    onChange={handleProfilePicChange}
                  />
                  <label
                    htmlFor="profile-pic-upload"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      borderRadius: "50px",
                      background: "linear-gradient(145deg, #f0f0f0, #ffffff)",
                      boxShadow: "3px 3px 6px #d9d9d9, -3px -3px 6px #ffffff",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      position: "relative",
                      overflow: "hidden",
                      border: "2px solid rgba(0,0,0,0.1)",
                      ":hover": {
                        transform: "translateY(-2px)",
                        boxShadow:
                          "5px 5px 10px #d9d9d9, -5px -5px 10px #ffffff",
                      },
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "24px",
                        height: "24px",
                        backgroundColor: colors.primary_dark,
                        borderRadius: "50%",
                        position: "relative",
                      }}
                    >
                      <UploadFile
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "white",
                          fontSize: "14px",
                        }}
                      />
                    </span>
                    <span
                      style={{
                        color: colors.primary_dark,
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        fontWeight: "600",
                      }}
                    >
                      {t("uploadNewAvatar")}
                    </span>
                    <div
                      style={{
                        position: "absolute",
                        top: "-50%",
                        left: "-50%",
                        width: "200%",
                        height: "200%",
                        background:
                          "linear-gradient(transparent, rgba(255,255,255,0.3), transparent)",
                        transform: "rotate(30deg)",
                        transition: "all 0.3s",
                        pointerEvents: "none",
                      }}
                      className="shimmer-effect"
                    />
                  </label>

                  <style jsx>{`
                    label:hover .shimmer-effect {
                      animation: shimmer 1.5s infinite;
                    }
                    @keyframes shimmer {
                      0% {
                        transform: translateX(-100%) rotate(30deg);
                      }
                      100% {
                        transform: translateX(100%) rotate(30deg);
                      }
                    }
                  `}</style>
                </>
              )}
            </Box>

            <Box>
              {/* First Line */}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? 2 : 10,
                  marginBottom: 3,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontFamily: fonts.poppins, color: colors.text }}
                  >
                    {t("firstname")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={!isEditing}
                    sx={{
                      background: "white",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontFamily: fonts.poppins, color: colors.text }}
                  >
                    {t("lastname")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={!isEditing}
                    sx={{
                      background: "white",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                </Box>
              </Box>

              {/* Repeat for other lines */}
              {[
                {
                  label1: "phone",
                  label2: "Email",
                  name1: "phoneNumber",
                  name2: "email",
                },
                {
                  label1: "gender",
                  label2: "age",
                  name1: "gender",
                  name2: "age",
                },
                {
                  label1: "role",
                  label2: "country",
                  name1: "role",
                  name2: "country",
                },
                {
                  label1: "state",
                  label2: "tahsil",
                  name1: "state",
                  name2: "tahsil",
                },
              ].map(({ label1, label2, name1, name2 }, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 2 : 10,
                    marginBottom: 3,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontFamily: fonts.poppins, color: colors.text }}
                    >
                      {t(label1)}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name={name1}
                      value={formData[name1]}
                      onChange={handleChange}
                      disabled={!isEditing}
                      sx={{
                        background: "white",
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontFamily: fonts.poppins, color: colors.text }}
                    >
                      {t(label2)}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name={name2}
                      value={formData[name2]}
                      onChange={handleChange}
                      disabled={!isEditing}
                      sx={{
                        background: "white",
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                      }}
                    />
                  </Box>
                </Box>
              ))}

              {/* Save & Cancel Buttons */}
              {isEditing && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "flex-end",
                    gap: 2,
                    marginTop: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => setIsEditing((prev) => !prev)}
                    sx={{
                      fontFamily: fonts.poppins,
                      backgroundImage: colors.button,
                      borderRadius: 2,
                      padding: "8px 20px",
                      fontSize: size.formLabel,
                      textTransform: "none",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                      fontFamily: fonts.poppins,
                      backgroundImage: colors.button,
                      borderRadius: 2,
                      padding: "8px 20px",
                      fontSize: size.formLabel,
                      textTransform: "none",
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Profile;
